/**
 * node-latency.js — на сколько отсчётов узел Web Audio сдвигает звук (window.NodeLatency).
 *
 * Зачем (2026-10-09, обращение с /de/equalizer, альбом для сведения в 5.1):
 * офлайн-рендер длиной ровно в исходник через узел с задержкой отдаёт файл,
 * который начинается с тишины и теряет свой хвост. DynamicsCompressorNode
 * держит упреждение 6 мс (Chromium 147 и 155: 264 отсчёта при 44.1 кГц, 288
 * при 48, 576 при 96 — при ЛЮБЫХ параметрах, включая ratio 1); WaveShaper ×4 —
 * 192 отсчёта, ×2 — 128. На проде: /equalizer 288 при 48 кГц, /audio-mixer 288
 * (с мастер-компрессором 576), /music-mastering 132. Каждый трек альбома без
 * пауз получал щелчок на стыке.
 *
 * Число не зашивается: оно своё у движка (в Firefox своя передискретизация).
 * Замер — импульс −40 dBFS (ниже любого порога компрессора, в линейной зоне
 * кривой) через узел в OfflineAudioContext, место максимума на выходе. Один раз
 * на (частота, ключ), ~5 мс.
 *
 *   var lead = await NodeLatency.compressor(sr);           // упреждение компрессора
 *   var n    = await NodeLatency.measure(sr, 'my-node', function (ctx) { return ctx.createWaveShaper(); });
 *
 * Как применять в рендере: длина = исходник + задержка, голова длиной в
 * задержку отрезается (образец — renderWithEq в /equalizer). В живом графе
 * параллельные ветки с разной задержкой выравниваются NodeLatency.delay(ctx, n).
 */
(function () {
    'use strict';
    if (window.NodeLatency) return;

    var cache = {};
    function measure(sr, key, make) {
        var k = sr + '|' + key;
        if (cache[k]) return cache[k];
        cache[k] = (async function () {
            try {
                var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
                var n = 4096, ctx = new OAC(1, n, sr), b = ctx.createBuffer(1, n, sr);
                b.getChannelData(0)[0] = 0.01;
                var s = ctx.createBufferSource(); s.buffer = b;
                var node = make(ctx); s.connect(node); node.connect(ctx.destination); s.start(0);
                var y = (await ctx.startRendering()).getChannelData(0), at = 0;
                for (var i = 1; i < n; i++) if (Math.abs(y[i]) > Math.abs(y[at])) at = i;
                return at;
            } catch (_) { delete cache[k]; return 0; }
        })();
        return cache[k];
    }
    function compressor(sr) {
        return measure(sr, 'dynamics-compressor', function (ctx) { return ctx.createDynamicsCompressor(); });
    }

    /* DelayNode ровно на frames отсчётов — для ветки, которая идёт МИМО
       задерживающих узлов и должна сойтись с теми, что через них (саб-слой
       /audio-mixer мимо компрессора). Целое число отсчётов задержка повторяет
       без интерполяции. */
    function delay(ctx, frames) {
        var sec = Math.max(0, frames || 0) / ctx.sampleRate;
        var d = ctx.createDelay(Math.max(1, sec + 0.01));
        d.delayTime.value = sec;
        return d;
    }

    /* WaveShaper с передискретизацией ('2x' / '4x'): линейная кривая, та же
       задержка, что у любой кривой этого узла (Chromium: 128 / 192 отсчёта). */
    function shaper(sr, oversample) {
        return measure(sr, 'waveshaper-' + oversample, function (ctx) {
            var w = ctx.createWaveShaper(); w.curve = new Float32Array([-1, 1]); w.oversample = oversample; return w;
        });
    }

    /* Рендер был длиннее исходника на lead отсчётов: голова длиной lead
       отрезается, длина возвращается исходная (length). lead 0 и буфер ровно
       length — тот же буфер; рендер «с запасом» (/record: цепочка известна после
       сборки) режется до length и при lead 0. */
    function trimHead(buf, lead, length) {
        lead = lead || 0;
        if (!lead && buf.length <= length) return buf;
        var n = Math.max(0, Math.min(length, buf.length - lead)), nCh = buf.numberOfChannels, out;
        try { out = new AudioBuffer({ numberOfChannels: nCh, length: Math.max(1, n), sampleRate: buf.sampleRate }); }
        catch (_) {
            var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
            out = new OAC(nCh, Math.max(1, n), buf.sampleRate).createBuffer(nCh, Math.max(1, n), buf.sampleRate);
        }
        for (var c = 0; c < nCh; c++) out.getChannelData(c).set(buf.getChannelData(c).subarray(lead, lead + n));
        return out;
    }

    /* Путь «как через WaveShaper, но без искажения» — для СУХОЙ ветки, которая
       складывается с веткой через WaveShaper с передискретизацией: тот же узел
       с линейной кривой даёт ту же задержку в любом движке (сумма без гребёнки).
       ×1/8 до и ×8 после: кривая жёстко режет за ±1, так путь линеен до +18 dBFS.
       Образец — AudioSaturation.alignedDry; /voice-changer — сухая ветка искажения. */
    function alignedPath(ctx, oversample) {
        var pre = ctx.createGain(); pre.gain.value = 0.125;
        var ws = ctx.createWaveShaper(); ws.curve = new Float32Array([-1, 1]); ws.oversample = oversample || '4x';
        var post = ctx.createGain(); post.gain.value = 8;
        pre.connect(ws); ws.connect(post);
        return { input: pre, output: post };
    }

    window.NodeLatency = { measure: measure, compressor: compressor, shaper: shaper, delay: delay, trimHead: trimHead, alignedPath: alignedPath };
})();
